import React, { useState } from 'react';
import { Apple, AlertTriangle, ChevronDown, ChevronUp, Utensils, Scale, Pill, Clock, Sun, BookOpen, HeartHandshake } from 'lucide-react';

/**
 * Preview Eating Well Page
 * Read-only version with collapsible sections
 */

const EATING_SECTIONS = [
  {
    id: 'eating-enough',
    title: 'Eating Enough',
    icon: Scale,
    content: `For people living with CLL, maintaining adequate nutrition is often more important than following restrictive diets. Weight loss can sometimes be a concern, particularly during or after treatment.

The goal is generally to eat enough to maintain your weight and energy levels. This doesn't mean eating perfectly — it means eating sufficiently.`
  },
  {
    id: 'food-over-supplements',
    title: 'Food Over Supplements',
    icon: Apple,
    content: `Where possible, getting nutrients from food is generally preferred over relying on supplements. Whole foods contain combinations of nutrients that work together in ways that supplements may not replicate.`
  },
  {
    id: 'variety',
    title: 'Variety of Foods',
    icon: Utensils,
    content: `Eating a variety of foods helps ensure you get a range of nutrients. This includes different colored fruits and vegetables, various protein sources, whole grains, and dairy or alternatives as needed.`
  },
  {
    id: 'small-frequent-meals',
    title: 'Small Frequent Meals',
    icon: Clock,
    content: `When appetite is reduced or eating large meals feels difficult, smaller meals eaten more frequently can be helpful. This approach can feel more manageable when energy is low.`
  },
  {
    id: 'protein-guidance',
    title: 'Protein Guidance',
    icon: Utensils,
    content: `Protein is important for maintaining muscle mass and supporting recovery. Good protein sources include meat, poultry, fish, eggs, dairy products, legumes, and nuts.`
  },
  {
    id: 'citrus-interactions',
    title: 'Citrus & Medication Interactions',
    icon: Pill,
    content: `Some citrus fruits, particularly grapefruit, can interact with certain medications. If you're taking any medications, it's worth checking with your pharmacist about potential food interactions.`
  },
  {
    id: 'fatigue-eating',
    title: 'Fatigue-Friendly Eating',
    icon: Clock,
    content: `Fatigue can make cooking and eating challenging. Strategies like preparing food when energy is higher and keeping easy-to-grab snacks accessible can help.`
  },
  {
    id: 'vitamin-d',
    title: 'Vitamin D Discussion',
    icon: Sun,
    content: `Vitamin D is a topic often discussed in the context of CLL. Whether you should have your levels tested or take supplements is a conversation to have with your doctor.`
  },
  {
    id: 'evidence-based',
    title: 'Evidence-Based Nutrition',
    icon: BookOpen,
    content: `Evidence-based nutrition focuses on well-researched principles rather than trends or anecdotes. Be cautious of claims that sound too good to be true.`
  },
  {
    id: 'professional-help',
    title: 'When to Seek Professional Help',
    icon: HeartHandshake,
    content: `Consider speaking with a registered dietitian if you're experiencing unintentional weight loss, eating has become difficult, or you want personalized nutrition guidance.`
  }
];

const PreviewEatingWellPage = () => {
  const [expandedSections, setExpandedSections] = useState(['eating-enough']);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Apple className="h-8 w-8 text-primary" />
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900">
            Eating Well With CLL
          </h1>
        </div>
        <p className="text-lg leading-relaxed text-slate-700">
          Supportive nutrition information for your journey
        </p>
      </div>

      {/* Educational Introduction */}
      <div className="bg-gradient-to-br from-teal-50 to-blue-50 border border-teal-200 rounded-2xl p-6 md:p-8">
        <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-4">
          About this section
        </h2>
        <div className="space-y-4 text-base md:text-lg text-slate-700 leading-relaxed">
          <p>
            This section provides general information about nutrition for people living with CLL. 
            The focus is on practical, supportive approaches rather than strict rules or diets.
          </p>
          <p>
            Everyone's situation is different. What works for one person may not be right for 
            another. These are starting points for conversations with your healthcare team.
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
        <div className="flex gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-semibold text-amber-900 mb-2">Important Notice</h3>
            <p className="text-amber-800 leading-relaxed">
              This information is for general education only. Always discuss significant 
              dietary changes with your medical team, especially during treatment.
            </p>
          </div>
        </div>
      </div>

      {/* Collapsible Content Sections - Read-only appearance */}
      <div className="space-y-4 opacity-75">
        {EATING_SECTIONS.map((section) => {
          const Icon = section.icon;
          const isExpanded = expandedSections.includes(section.id);
          
          return (
            <div key={section.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-50 rounded-lg">
                    <Icon className="h-5 w-5 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 text-left">
                    {section.title}
                  </h3>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-slate-400" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-slate-400" />
                )}
              </button>
              
              {isExpanded && (
                <div className="px-5 pb-5 pt-0">
                  <div className="pl-12 border-l-2 border-teal-100">
                    <p className="text-slate-700 leading-relaxed mb-2">
                      {section.content}
                    </p>
                    <p className="text-xs text-slate-400">Full content available in app</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PreviewEatingWellPage;
